import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { DeviceService } from 'src/app/services/device.service';
import { SiteService } from 'src/app/services/site.service';
import { UserService } from 'src/app/services/user.service';
import { AppState } from 'src/app/store/app.reducer';
import * as SiteMapActions from './site-map.actions';
import { TranslateService } from '@ngx-translate/core';


@Injectable()
export class SiteMapEffects {
    getSites$ = createEffect(() => this.actions$.pipe(
        ofType(SiteMapActions.getSitesData),
        withLatestFrom(this.store.select('user', 'currentUser')),
        switchMap(([action, currentUser]) => this.siteService.getSites().pipe(
            map((resp) => {
                if (resp.Success) {
                    const newSites = resp.Data.map(site => ({ ...site, CurrentlyOwned: site.UserId === currentUser.Id }));
                    return SiteMapActions.getSitesDataSuccess({ sites: newSites });
                } else {
                    return SiteMapActions.getSitesDataFailed({ errorMessage: resp.ErrorMessage });
                }
            }),
            catchError(error => {
                console.error(error);
                return of(SiteMapActions.getSitesDataFailed({ errorMessage: error.message }));
            })
        ))
    ));

    loadSitesStatus$ = createEffect(() => this.actions$.pipe(
        ofType(SiteMapActions.getSitesDataSuccess),
        switchMap((action) => action.sites.map(site => SiteMapActions.getSiteStatusData({ site })))
    ));

    loadSitesOwner$ = createEffect(() => this.actions$.pipe(
        ofType(SiteMapActions.getSitesDataSuccess),
        switchMap((action) => action.sites.map(site => SiteMapActions.getSiteOwner({ site })))
    ));

    getSiteStatus$ = createEffect(() => this.actions$.pipe(
        ofType(SiteMapActions.getSiteStatusData),
        mergeMap((action) => {
            const deviceIds = action.site.Devices.map(d => d.Id);
            if (deviceIds.length === 0) {
                return of(SiteMapActions.getSiteStatusDataFailed({ errorMessage: 'no devices in site' }));
            }
            return this.deviceService.getDevicesStatus(deviceIds)
                .pipe(
                    map(resp => {
                        if (resp.Success) {
                            return SiteMapActions.getSiteStatusDataSuccess({ siteId: action.site.Id, deviceStatus: resp.Data });
                        } else {
                            return SiteMapActions.getSiteStatusDataFailed({ errorMessage: resp.ErrorMessage });
                        }
                    }),
                    catchError(error => of(SiteMapActions.getSiteStatusDataFailed({ errorMessage: error.message })))
                );
        })
    ));

    getSiteOwner$ = createEffect(() => this.actions$.pipe(
        ofType(SiteMapActions.getSiteOwner),
        mergeMap((action) => this.userService.getUserById(action.site.UserId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return SiteMapActions.getSiteOwnerSuccess({ siteId: action.site.Id, user: resp.Data });
                    } else {
                        return SiteMapActions.getSiteOwnerFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(SiteMapActions.getSiteOwnerFailed({ errorMessage: error.message })))
            ))
    ));

    getSitesExtTechAcl$ = createEffect(() => this.actions$.pipe(
        ofType(SiteMapActions.getSitesExtTechAcl),
        withLatestFrom(this.store.select('user', 'currentUser')),
        switchMap(([action, currentUser]) => this.siteService.getSitesExtendedWithTechnicianIdReqOpen().pipe(
            map((resp) => {
                if (resp.Success) {
                    const newSites = resp.Data.map(site => ({ ...site, CurrentlyOwned: site.UserId === currentUser.Id, GeoLocalized: !!(site.Latitude && site.Longitude) && (site.Latitude !== 0 && site.Longitude !== 0) }));
                    return SiteMapActions.getSitesExtTechAclSuccess({ sites: newSites });
                } else {
                    return SiteMapActions.getSitesExtTechAclFailed({ errorMessage: resp.ErrorMessage });
                }
            }),
            catchError(error => {
                console.error(error);
                return of(SiteMapActions.getSitesExtTechAclFailed({ errorMessage: error.message }));
            })
        ))
    ));

    getLocalsites$ = createEffect(() => this.actions$.pipe(
        ofType(SiteMapActions.getLocalsites),
        switchMap((action) => this.siteService.getLocalSites().pipe(
            map((resp) => {
                if (resp !== undefined) {
                    const localsites = resp.map(localsite => ({ ...localsite, GeoLocalized: !!(localsite.Latitude && localsite.Longitude) && (localsite.Latitude !== 0 && localsite.Longitude !== 0) }));
                    return SiteMapActions.getLocalsitesSuccess({ localsites: localsites });
                } else {
                    return SiteMapActions.getLocalsitesFailed({ errorMessage: this.translate.instant('ERROR_GET_LOCAL_SITES') });
                }
            }),
            catchError(error => {
                console.error(error);
                return of(SiteMapActions.getLocalsitesFailed({ errorMessage: error.message }));
            })
        ))
    ));

    constructor(
        private actions$: Actions,
        private store: Store<AppState>,
        private siteService: SiteService,
        private deviceService: DeviceService,
        private userService: UserService,
        private translate: TranslateService,
    ) { }
}
